import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectAvailableOrganizations } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';

type ReportParamsGetDateRange = {
  dateRange: string;
  startDate?: string;
  endDate?: string;
};

export type GetDateRangeFn = () => ReportParamsGetDateRange;

type ReportParamsContextType = {
  deleteParams: (keys: string[]) => void;
  updateOrgIds: (orgIds: string[]) => void;
  getOrgIds: () => string[];
  getDateRange: GetDateRangeFn;
  updateDateRange: (value: string) => void;
  handleDateRangeChange: (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => void;
};

const ReportParamsContext = createContext<ReportParamsContextType | undefined>(
  undefined
);

export function ReportParamsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const availableOrganizations = useAppSelector(selectAvailableOrganizations); // TODO-- modify this by user role

  // Set default parameters if none are present
  useEffect(() => {
    const hasDateRange = searchParams.has('dateRange');
    const hasOrgIds = searchParams.has('orgIds');

    if (!hasDateRange || !hasOrgIds) {
      const newParams = new URLSearchParams(searchParams);

      if (!hasDateRange) {
        newParams.set('dateRange', 'last30days');
      }

      if (!hasOrgIds) {
        newParams.set(
          'orgIds',
          availableOrganizations.map(org => org.id).join(',')
        );
      }

      setSearchParams(newParams, { replace: true });
    }
  }, []);

  const updateParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (!value) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  const deleteParams = (keys: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    keys.forEach(key => {
      newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const updateOrgIds = (orgIds: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('orgIds', orgIds.join(','));
    setSearchParams(newParams, { replace: true });
  };

  const getOrgIds = () => {
    const orgIdsParam = searchParams.get('orgIds') || '';
    return orgIdsParam ? orgIdsParam.split(',').filter(Boolean) : [];
  };

  const getDateRange = () => {
    const dateRange = searchParams.get('dateRange') || '';
    if (dateRange === 'custom') {
      const startDate = searchParams.get('startDate') || '';
      const endDate = searchParams.get('endDate') || '';
      return { dateRange, startDate, endDate };
    }
    return { dateRange };
  };

  const updateDateRange = (dateRange: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('dateRange', dateRange);

    // Clear custom date range params when switching to a preset
    if (dateRange !== 'custom') {
      newParams.delete('startDate');
      newParams.delete('endDate');
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleDateRangeChange = useMemo(
    () => (startDate: Date | undefined, endDate: Date | undefined) => {
      if (startDate && endDate) {
        const params: Record<string, string> = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          dateRange: 'custom'
        };
        updateParams(params);
      }
    },
    [updateDateRange, updateParams]
  );

  const value = useMemo(
    () => ({
      deleteParams,
      getDateRange,
      updateDateRange,
      handleDateRangeChange,
      getOrgIds,
      updateOrgIds
    }),
    [
      deleteParams,
      getDateRange,
      updateDateRange,
      handleDateRangeChange,
      getOrgIds,
      updateOrgIds
    ]
  );

  return (
    <ReportParamsContext.Provider value={value}>
      {children}
    </ReportParamsContext.Provider>
  );
}

export function useReportParams() {
  const context = useContext(ReportParamsContext);
  if (!context) {
    throw new Error(
      'useReportParams must be used within a ReportParamsProvider'
    );
  }
  return context;
}
